import { ElementRef } from '@angular/core';
import { claimButtonNaming, newNameSourceId } from './host-name-source';

/**
 * How far up from the host the search for a labelling row is allowed to go. Every row shape in
 * this app puts the words within a level or two of the control; going much further starts
 * naming a whole card instead of a row.
 */
const MAX_ROW_LEVELS = 4;

/**
 * The elements that count as "another control in this row" and stop the search. A link is
 * deliberately NOT here: a link inside the words of a label ("I accept the Terms") is part of
 * what the row says, not a second thing to operate.
 */
const OTHER_CONTROL_SELECTOR = 'button, input, select, textarea, [cs-capture-cover]';

/**
 * Remembers which row each button was named from, so the walk up the tree runs once instead of
 * on every change-detection pass. Keyed by the button, so an element that goes away is dropped.
 */
const resolvedRowByButton = new WeakMap<Element, HTMLElement>();

/**
 * Gives a component's inner `<button cs-capture-cover>` an accessible name taken from the row
 * the component sits in, by pointing its `aria-labelledby` at that row.
 *
 * This is the sibling of `syncButtonNameFromHost`, for the controls that cannot use it. A
 * checkbox draws a box, a toggle draws a track, and a select draws only its current value — so
 * none of them draws the words that say what the control is FOR. Those words sit beside the
 * control, in whatever row encloses it: a `cs-item` with a `cs-label`, or an ad-hoc
 * `<div class="toggle-row"><span>Show calories</span><cs-toggle …></div>`. Naming from the host
 * would give a checkbox or a toggle an empty name and a select only its own value.
 *
 * So this walks UP from the host instead, and takes the nearest ancestor that both says
 * something of its own and holds no other control. That is the same span an implicit
 * `<label>` wrap would cover, and the "no other control" part is what keeps the name from
 * swallowing a neighbouring row: a column of checkboxes holds several controls, so the walk
 * stops below it and lands on the one row.
 *
 * When no such ancestor exists the button is left alone rather than named from a guess. A call
 * site in that position is expected to pass an explicit `ariaLabel`, which wins here exactly as
 * it does in `syncButtonNameFromHost`.
 *
 * Call it from the component's `ngAfterViewChecked`, for the same reason the host version says:
 * the surrounding markup can change at any time, and a name source pointing at a row the
 * control has since left names the wrong thing.
 */
export function syncButtonNameFromRow(elementRef: ElementRef, ariaLabel: string | undefined): void {
  const host = elementRef.nativeElement as HTMLElement;
  const button = host.querySelector(':scope > button[cs-capture-cover]') as HTMLElement | null;
  // if the template has not rendered its button, there is nothing to name
  if (!button) {
    return;
  }
  claimButtonNaming(button);
  // if the caller gave an explicit name, it is on the button already as aria-label
  if (ariaLabel) {
    button.removeAttribute('aria-labelledby');
    resolvedRowByButton.delete(button);
    return;
  }
  // if the row found last time still qualifies, there is nothing to redo
  if (isRememberedRowStillRight(button, host)) {
    return;
  }
  const row = findLabellingRow(host);
  if (!row) {
    // nothing around this control says what it is for, so any name here would be invented.
    // drop the link as well as the memory: a row that qualified before and does not now must
    // stop naming this control, rather than go on reading out whatever it has become
    button.removeAttribute('aria-labelledby');
    resolvedRowByButton.delete(button);
    return;
  }
  // if the row has no id yet, give it one nothing else uses
  if (!row.id) {
    row.id = newNameSourceId();
  }
  if (button.getAttribute('aria-labelledby') !== row.id) {
    button.setAttribute('aria-labelledby', row.id);
  }
  resolvedRowByButton.set(button, row);
}

/**
 * Whether the row remembered for `button` is still the row this control should be named from.
 *
 * What the cache saves is the WALK up the tree, never the two rules that decide whether a row may
 * name a control — those are re-checked on every pass against the row as it is NOW. The row's
 * contents change under us: a `cs-tooltip` or a remove button behind an `@if` can appear in a row
 * that held nothing but this control when the row was first resolved, and `cs-tooltip` renders a
 * capture-cover button of its own. Trusting the remembered answer through that would leave the
 * control named after a row that now reads out a neighbouring control's text — the one thing the
 * "no other control" rule exists to prevent.
 */
function isRememberedRowStillRight(button: HTMLElement, host: HTMLElement): boolean {
  const remembered = resolvedRowByButton.get(button);
  if (!remembered || !remembered.isConnected || !remembered.contains(button)) {
    return false;
  }
  if (button.getAttribute('aria-labelledby') !== remembered.id) {
    return false;
  }
  return !containsControlOutsideHost(remembered, host) && hasTextOutsideHost(remembered, host);
}

/**
 * The nearest ancestor of `host` that says something of its own and holds no control other than
 * `host`, or `null` when there is none within reach.
 */
function findLabellingRow(host: HTMLElement): HTMLElement | null {
  let candidate = host.parentElement;
  for (let level = 0; candidate && level < MAX_ROW_LEVELS; level++) {
    // a second control means this is a list of rows, not one row — stop rather than climb past it
    if (containsControlOutsideHost(candidate, host)) {
      return null;
    }
    if (hasTextOutsideHost(candidate, host)) {
      return candidate;
    }
    candidate = candidate.parentElement;
  }
  return null;
}

/**
 * Whether `candidate` holds a control that is not `host` and does not live inside it.
 */
function containsControlOutsideHost(candidate: HTMLElement, host: HTMLElement): boolean {
  const controls = candidate.querySelectorAll(OTHER_CONTROL_SELECTOR);
  for (let i = 0; i < controls.length; i++) {
    if (!host.contains(controls[i])) {
      return true;
    }
  }
  return false;
}

/**
 * Whether `candidate` draws any text that `host` did not draw itself.
 *
 * `host` is always inside `candidate`, so every character the host draws is also counted in the
 * candidate's text. Comparing the two lengths therefore says whether anything else is written
 * there, without having to walk the text nodes.
 */
function hasTextOutsideHost(candidate: HTMLElement, host: HTMLElement): boolean {
  const candidateText = (candidate.textContent || '').trim();
  if (!candidateText) {
    return false;
  }
  const hostText = (host.textContent || '').trim();
  return candidateText.length > hostText.length;
}
